"use client"

import type { HttpTypes } from "@medusajs/types"
import { useEffect, useState, type ReactNode } from "react"
import { useProductPurchase } from "../../context/product-purchase-context"
import { useProductPurchaseIntent } from "../../hooks/use-product-purchase-intent"
import { PRODUCT_COPY } from "../../lib/product-copy"
import { formatProductMoney, productDiscountPercentage } from "../../lib/product-money"
import { shortProductName, splitProductTitle } from "../../lib/product-title"
import type { PurchaseSelection, StoreBundle, StoreRichTextDocument } from "../../types"
import BundleSelector from "../bundle-selector"
import OptionSelect from "../product-actions/option-select"
import ProductDescription from "../product-description"
import PaymentBadges from "../product-main/payment-badges"
import GarantiSecurePayment from "@modules/common/components/garanti-secure-payment"
import StockUrgency from "../product-main/stock-urgency"
import styles from "./product-purchase-panel.module.css"

type Props = {
  product: HttpTypes.StoreProduct
  bundles: StoreBundle[]
  trustSlot?: ReactNode
  mobileGallerySlot?: ReactNode
  headline?: string | null
  description?: StoreRichTextDocument | null
  purchasePanelId: string
  primaryActionId: string
}

const optionsAsKeymap = (
  variantOptions: HttpTypes.StoreProductVariant["options"] | undefined
) =>
  variantOptions?.reduce((acc: Record<string, string>, option) => {
    if (option.option_id) acc[option.option_id] = option.value
    return acc
  }, {}) ?? {}

const sameOptions = (a: Record<string, string>, b: Record<string, string>) => {
  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)
  return (
    aKeys.length === bKeys.length &&
    aKeys.every((key) => a[key] === b[key])
  )
}

const ProductPurchasePanel = ({
  product,
  bundles,
  trustSlot,
  mobileGallerySlot,
  headline,
  description,
  purchasePanelId,
  primaryActionId,
}: Props) => {
  const purchase = useProductPurchase()
  const selection = purchase.selection
  const selectedVariant =
    selection.kind === "single"
      ? product.variants?.find(
          (variant) => variant.id === selection.variant_id
        )
      : undefined
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>(
    () => optionsAsKeymap(selectedVariant?.options)
  )

  useEffect(() => {
    if (selection.kind !== "single") return
    const variantId = selection.variant_id
    const variant = product.variants?.find(
      (candidate) => candidate.id === variantId
    )
    if (variant) setSelectedOptions(optionsAsKeymap(variant.options))
  }, [product.variants, selection])

  const updateOption = (optionId: string, value: string) => {
    const next = { ...selectedOptions, [optionId]: value }
    setSelectedOptions(next)
    const variant = product.variants?.find((candidate) =>
      sameOptions(optionsAsKeymap(candidate.options), next)
    )
    purchase.selectSingle(variant?.id ?? "")
  }

  const onBundleSelectionChange = (selection: PurchaseSelection) => {
    if (selection.kind === "single") {
      purchase.selectSingle(selection.variant_id)
    } else if (selection.kind === "bundle") {
      purchase.selectBundle(selection)
    }
  }

  const intent = useProductPurchaseIntent(purchasePanelId)
  const showOptions =
    (product.variants?.length ?? 0) > 1 &&
    (product.options?.length ?? 0) > 0
  const titleLines = headline?.trim()
    ? [shortProductName(product.title ?? ""), headline.trim()]
    : splitProductTitle(product.title ?? "")
  const priceVariant =
    selectedVariant ??
    product.variants?.find(
      (variant) =>
        !variant.manage_inventory ||
        Boolean(variant.allow_backorder) ||
        Number(variant.inventory_quantity ?? 0) > 0
    ) ??
    product.variants?.[0]
  const calculatedPrice = priceVariant?.calculated_price
  const currentAmount = calculatedPrice?.calculated_amount == null
    ? null
    : Number(calculatedPrice.calculated_amount)
  const compareAtAmount = calculatedPrice?.original_amount == null
    ? null
    : Number(calculatedPrice.original_amount)
  const currencyCode = String(calculatedPrice?.currency_code ?? "try").toLowerCase()
  const discountPercentage = currentAmount == null || compareAtAmount == null
    ? null
    : productDiscountPercentage(compareAtAmount, currentAmount)

  return (
    <section
      id={purchasePanelId}
      className={styles.panel}
      tabIndex={-1}
      aria-label="Satın alma seçenekleri"
    >
      <header className={styles.header}>
        <h1 className={styles.title}>
          {titleLines.map((line, index) => (
            <span
              key={`${line}-${index}`}
              className={styles.titleLine}
              data-product-title-line={index === 0 ? "primary" : "secondary"}
            >
              {line}
            </span>
          ))}
        </h1>
      </header>

      <div className={styles.priceSection} data-testid="product-price">
        {currentAmount !== null ? (
          <>
            <div className={styles.productPriceOnSale}>
              <span className={styles.price}>{formatProductMoney(currentAmount, currencyCode)}</span>
              {compareAtAmount !== null && compareAtAmount > currentAmount ? (
                <s className={styles.compareAtPrice}>{formatProductMoney(compareAtAmount, currencyCode)}</s>
              ) : null}
            </div>
            {discountPercentage !== null ? (
              <span className={styles.discountBadge}>
                <svg className={styles.discountIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z" />
                  <circle cx="7.5" cy="7.5" r=".5" fill="currentColor" />
                </svg>
                {discountPercentage}% indirim
              </span>
            ) : null}
          </>
        ) : null}
      </div>

      <div className={styles.separator} aria-hidden="true" />

      <ProductDescription document={description} />

      {showOptions ? (
        <div className={styles.variantOptions}>
          {product.options!.map((option) => (
            <OptionSelect
              key={option.id}
              option={option}
              current={selectedOptions[option.id]}
              updateOption={updateOption}
              title={option.title ?? "Seçenek"}
              disabled={purchase.status === "adding"}
              invalid={!purchase.availability.complete && !selectedOptions[option.id]}
              data-testid="product-options"
            />
          ))}
        </div>
      ) : null}

      {mobileGallerySlot}

      <BundleSelector
        product={product}
        bundles={bundles}
        selection={purchase.selection}
        onSelectionChange={onBundleSelectionChange}
      />

      <button
        id={primaryActionId}
        type="button"
        className={styles.cta}
        disabled={intent.disabled}
        aria-label={intent.primaryLabel}
        onClick={intent.run}
      >
        {intent.primaryLabel}
      </button>

      <div className={styles.securePayment}>
        <GarantiSecurePayment />
      </div>

      <div className={styles.error} aria-live="polite">
        {purchase.status === "error"
          ? purchase.error
          : !purchase.availability.available
            ? purchase.availability.reason
            : null}
      </div>

      <PaymentBadges />

      <StockUrgency text={PRODUCT_COPY.stockUrgency} />

      {trustSlot ? <div className={styles.trustSlot}>{trustSlot}</div> : null}
    </section>
  )
}

export default ProductPurchasePanel
